import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { FaInfoCircle, FaChevronLeft, FaChevronRight, FaDownload, FaSave, FaTrash, FaTimes, FaGripVertical } from 'react-icons/fa'
import { HiDocumentReport } from 'react-icons/hi'

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL
const token = () => sessionStorage.getItem('userToken')

// Info tooltip component
function InfoTip({ text }) {
    const [show, setShow] = useState(false)
    const [pos, setPos] = useState({ top: 0, left: 0 })
    const iconRef = useRef(null)

    const handleEnter = () => {
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect()
            setPos({
                top: rect.top - 8,
                left: rect.left + rect.width / 2,
            })
        }
        setShow(true)
    }

    return (
        <span className="inline-block ml-1" ref={iconRef}>
            <FaInfoCircle
                className="w-3.5 h-3.5 text-gray-400 hover:text-teal-600 cursor-help inline"
                onMouseEnter={handleEnter}
                onMouseLeave={() => setShow(false)}
            />
            {show && (
                <span
                    className="fixed z-[9999] px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg w-56 text-left leading-relaxed pointer-events-none -translate-x-1/2 -translate-y-full"
                    style={{ top: pos.top, left: pos.left }}
                >
                    {text}
                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                </span>
            )}
        </span>
    )
}

export default function ReportsPage() {
    const [step, setStep] = useState('landing') // 'landing' | 'scope' | 'preview'
    const [templates, setTemplates] = useState([])
    const [scopeOptions, setScopeOptions] = useState({ spaces: [], projects: [] })
    const [columnGroups, setColumnGroups] = useState({})

    // Scope state
    const [scopeType, setScopeType] = useState('all')
    const [selectedSpaces, setSelectedSpaces] = useState([])
    const [selectedProjects, setSelectedProjects] = useState([])

    // Preview state
    const [columns, setColumns] = useState(['filename', 'label', 'space_path', 'project'])
    const [columnOrder, setColumnOrder] = useState(['filename', 'label', 'space_path', 'project'])
    const [rows, setRows] = useState([])
    const [page, setPage] = useState(1)
    const [pageSize] = useState(25)
    const [total, setTotal] = useState(0)
    const [totalPages, setTotalPages] = useState(1)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [columnCounts, setColumnCounts] = useState({}) // { col_key: count_with_data }

    // Template save modal
    const [showSaveModal, setShowSaveModal] = useState(false)
    const [templateName, setTemplateName] = useState('')
    const [templateDesc, setTemplateDesc] = useState('')
    const [saveScope, setSaveScope] = useState(true)

    // Export dropdown
    const [showExportMenu, setShowExportMenu] = useState(false)

    // Load initial data
    useEffect(() => {
        fetchTemplates()
        fetchScopeOptions()
        fetchColumns()
    }, [])

    // Accept prescope from the tariff-modal redirect (WS2 / 4a): ?space=&project=
    // or router state { state: { space_path, folder_name } }.
    const location = useLocation()
    const [prescopeApplied, setPrescopeApplied] = useState(false)
    useEffect(() => {
        if (prescopeApplied) return
        const hasOptions =
            (scopeOptions.projects && scopeOptions.projects.length > 0) ||
            (scopeOptions.spaces && scopeOptions.spaces.length > 0)
        if (!hasOptions) return
        const params = new URLSearchParams(location.search || '')
        const stateScope = location.state?.state || {}
        const space = params.get('space') || stateScope.space_path
        const project = params.get('project') || stateScope.folder_name
        if (!space && !project) { setPrescopeApplied(true); return }
        let matched = null
        if (project) {
            matched = (scopeOptions.projects || []).find(
                (p) => p.folder_name === project && (!space || p.space_path === space)
            )
        }
        if (matched) {
            setScopeType('projects')
            setSelectedProjects([matched.id])
            setStep('scope')
        } else if (space) {
            setScopeType('spaces')
            setSelectedSpaces([space])
            setStep('scope')
        }
        setPrescopeApplied(true)
    }, [scopeOptions, location, prescopeApplied])

    const fetchTemplates = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/ocr/reports/templates/`, {
                headers: { Authorization: `Bearer ${token()}` }
            })
            setTemplates(res.data.templates || [])
        } catch (err) {
            console.error('Failed to fetch templates:', err)
        }
    }

    const fetchScopeOptions = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/ocr/reports/scope-options/`, {
                headers: { Authorization: `Bearer ${token()}` }
            })
            setScopeOptions(res.data)
        } catch (err) {
            console.error('Failed to fetch scope options:', err)
        }
    }

    const fetchColumns = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/ocr/reports/columns/`, {
                headers: { Authorization: `Bearer ${token()}` }
            })
            setColumnGroups(res.data.groups || {})
        } catch (err) {
            console.error('Failed to fetch columns:', err)
        }
    }

    const getScopeConfig = () => {
        if (scopeType === 'spaces') return { type: 'spaces', ids: selectedSpaces }
        if (scopeType === 'projects') return { type: 'projects', ids: selectedProjects }
        return { type: 'all' }
    }

    const fetchPreview = async (pageNum = 1, cols = columnOrder) => {
        setPreviewLoading(true)
        try {
            const res = await axios.post(`${BASE_URL}/ocr/reports/preview/`, {
                scope_config: getScopeConfig(),
                columns: cols,
                page: pageNum,
                page_size: pageSize,
            }, {
                headers: { Authorization: `Bearer ${token()}` }
            })
            setRows(res.data.rows || [])
            setTotal(res.data.total || 0)
            setTotalPages(res.data.total_pages || 1)
            setPage(res.data.page || 1)
            if (res.data.column_counts) {
                setColumnCounts(res.data.column_counts)
            }
        } catch (err) {
            console.error('Preview failed:', err)
            toast.error('Failed to load report preview')
        } finally {
            setPreviewLoading(false)
        }
    }

    const handleStartNew = () => {
        setScopeType('all')
        setSelectedSpaces([])
        setSelectedProjects([])
        setColumns(['filename', 'label', 'space_path', 'project'])
        setColumnOrder(['filename', 'label', 'space_path', 'project'])
        setStep('scope')
    }

    const handleLoadTemplate = (template) => {
        setScopeType(template.scope_config?.type || 'all')
        setSelectedSpaces(template.scope_config?.ids || [])
        setSelectedProjects(template.scope_config?.ids || [])
        setColumns(template.columns || ['filename', 'label'])
        setColumnOrder(template.column_order || template.columns || ['filename', 'label'])
        setStep('preview')
        setTimeout(() => fetchPreview(1, template.column_order || template.columns), 100)
    }

    const handleDeleteTemplate = async (id) => {
        try {
            await axios.delete(`${BASE_URL}/ocr/reports/templates/${id}/`, {
                headers: { Authorization: `Bearer ${token()}` }
            })
            setTemplates(prev => prev.filter(t => t.id !== id))
            toast.success('Template deleted')
        } catch (err) {
            toast.error('Failed to delete template')
        }
    }

    const handleNextToPreview = () => {
        setStep('preview')
        fetchPreview(1, columnOrder)
    }

    const toggleColumn = (key) => {
        let newCols, newOrder
        if (columns.includes(key)) {
            newCols = columns.filter(c => c !== key)
            newOrder = columnOrder.filter(c => c !== key)
        } else {
            newCols = [...columns, key]
            newOrder = [...columnOrder, key]
        }
        setColumns(newCols)
        setColumnOrder(newOrder)
        fetchPreview(1, newOrder)
    }

    const removeColumn = (key) => {
        const newCols = columns.filter(c => c !== key)
        const newOrder = columnOrder.filter(c => c !== key)
        setColumns(newCols)
        setColumnOrder(newOrder)
        fetchPreview(1, newOrder)
    }

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            fetchPreview(newPage, columnOrder)
        }
    }

    const handleSaveTemplate = async () => {
        if (!templateName.trim()) {
            toast.error('Please enter a template name')
            return
        }
        try {
            await axios.post(`${BASE_URL}/ocr/reports/templates/`, {
                name: templateName.trim(),
                description: templateDesc.trim(),
                scope_config: saveScope ? getScopeConfig() : { type: 'all' },
                columns: columns,
                column_order: columnOrder,
            }, {
                headers: { Authorization: `Bearer ${token()}` }
            })
            toast.success('Template saved!')
            setShowSaveModal(false)
            setTemplateName('')
            setTemplateDesc('')
            fetchTemplates()
        } catch (err) {
            toast.error('Failed to save template')
        }
    }

    const handleExport = async (format) => {
        setShowExportMenu(false)
        try {
            const res = await axios.post(`${BASE_URL}/ocr/reports/export/`, {
                scope_config: getScopeConfig(),
                columns: columns,
                column_order: columnOrder,
                format: format,
            }, {
                headers: { Authorization: `Bearer ${token()}` },
                responseType: 'blob',
            })

            const url = window.URL.createObjectURL(new Blob([res.data]))
            const link = document.createElement('a')
            link.href = url
            const ext = format === 'csv' ? 'csv' : 'xlsx'
            link.setAttribute('download', `report.${ext}`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            toast.success(`Report exported as ${ext.toUpperCase()}`)
        } catch (err) {
            toast.error('Export failed')
        }
    }

    // Get flat list of all column definitions for label lookup
    const allColumns = Object.values(columnGroups).flat()
    const getColLabel = (key) => {
        const col = allColumns.find(c => c.key === key)
        return col ? col.label : key
    }

    // ─────────────────────────────────────────────────────
    // RENDER: Landing
    // ─────────────────────────────────────────────────────
    if (step === 'landing') {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="max-w-5xl mx-auto">
                    <div className="flex items-center gap-3 mb-8">
                        <HiDocumentReport className="w-8 h-8 text-teal-700" />
                        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
                        <InfoTip text="Build custom reports by selecting a scope (which projects to include) and then choosing which data columns you want. Preview the data live, then export when you're satisfied." />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Build New */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
                            <h2 className="text-lg font-semibold text-gray-900 mb-2">Build New Report</h2>
                            <p className="text-sm text-gray-600 mb-4">
                                Select which projects to include, choose your data columns, preview the results in a live table, and export when ready.
                            </p>
                            <div className="text-xs text-gray-400 mb-4 space-y-1">
                                <p>• Choose scope: all projects, specific spaces, or individual projects</p>
                                <p>• Toggle columns from classification, tariff, design review, and more</p>
                                <p>• Preview paginated results before exporting</p>
                                <p>• Save your configuration as a reusable template</p>
                            </div>
                            <button
                                onClick={handleStartNew}
                                className="w-full py-2.5 bg-teal-700 text-white font-medium rounded-lg hover:bg-teal-800 transition-colors"
                            >
                                Start Building →
                            </button>
                        </div>

                        {/* Saved Templates */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                Saved Templates
                                <InfoTip text="Templates are shared with your entire organization. Anyone in your team can load and use a saved template to quickly regenerate a report." />
                            </h2>
                            {templates.length === 0 ? (
                                <p className="text-sm text-gray-400 italic mt-4">No templates saved yet. Build a report and save it as a template for quick access later.</p>
                            ) : (
                                <div className="space-y-3 mt-4 max-h-72 overflow-y-auto">
                                    {templates.map(t => (
                                        <div key={t.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-4 py-3 hover:bg-gray-50">
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">{t.name}</p>
                                                <p className="text-xs text-gray-500">
                                                    {t.columns?.length || 0} columns • by {t.created_by} • {new Date(t.updated_at).toLocaleDateString()}
                                                </p>
                                                {t.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{t.description}</p>}
                                            </div>
                                            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                                <button
                                                    onClick={() => handleLoadTemplate(t)}
                                                    className="px-3 py-1 text-xs font-medium bg-teal-50 text-teal-700 rounded hover:bg-teal-100 transition-colors"
                                                >
                                                    Load
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteTemplate(t.id)}
                                                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                    title="Delete template"
                                                >
                                                    <FaTrash className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ─────────────────────────────────────────────────────
    // RENDER: Scope Selection
    // ─────────────────────────────────────────────────────
    if (step === 'scope') {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="max-w-3xl mx-auto">
                    <button onClick={() => setStep('landing')} className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-6 text-sm">
                        <FaChevronLeft className="w-3 h-3" /> Back to Reports
                    </button>

                    <h1 className="text-xl font-bold text-gray-900 mb-1">Step 1: Select Scope</h1>
                    <p className="text-sm text-gray-500 mb-6">
                        Choose which data to include in your report.
                        <InfoTip text="'All projects' includes every drawing you have access to. 'Specific spaces' filters by org structure paths. 'Specific projects' lets you hand-pick individual project folders." />
                    </p>

                    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
                        {/* Scope type radio */}
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input type="radio" name="scope" value="all" checked={scopeType === 'all'} onChange={() => setScopeType('all')} className="text-teal-600" />
                            <span className="text-sm font-medium text-gray-800">All projects in my organization</span>
                            <InfoTip text="Includes every file across all projects you have access to. Best for org-wide summaries." />
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input type="radio" name="scope" value="spaces" checked={scopeType === 'spaces'} onChange={() => setScopeType('spaces')} className="text-teal-600" />
                            <span className="text-sm font-medium text-gray-800">Specific space path(s)</span>
                            <InfoTip text="Filter to one or more org structure paths (e.g., 'Manufacturing/CNC Parts'). All projects under those paths are included." />
                        </label>

                        {scopeType === 'spaces' && (
                            <div className="ml-7 mt-2">
                                <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                                    {scopeOptions.spaces.map(sp => (
                                        <label key={sp} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedSpaces.includes(sp)}
                                                onChange={(e) => {
                                                    if (e.target.checked) setSelectedSpaces(prev => [...prev, sp])
                                                    else setSelectedSpaces(prev => prev.filter(s => s !== sp))
                                                }}
                                                className="text-teal-600"
                                            />
                                            <span className="text-sm text-gray-700">{sp}</span>
                                        </label>
                                    ))}
                                    {scopeOptions.spaces.length === 0 && <p className="text-xs text-gray-400 italic p-2">No spaces found</p>}
                                </div>
                            </div>
                        )}

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input type="radio" name="scope" value="projects" checked={scopeType === 'projects'} onChange={() => setScopeType('projects')} className="text-teal-600" />
                            <span className="text-sm font-medium text-gray-800">Specific project(s)</span>
                            <InfoTip text="Hand-pick individual project folders. Best when you want to compare specific batches or deliverables." />
                        </label>

                        {scopeType === 'projects' && (
                            <div className="ml-7 mt-2">
                                <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                                    {scopeOptions.projects.map(proj => (
                                        <label key={proj.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedProjects.includes(proj.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) setSelectedProjects(prev => [...prev, proj.id])
                                                    else setSelectedProjects(prev => prev.filter(id => id !== proj.id))
                                                }}
                                                className="text-teal-600"
                                            />
                                            <span className="text-sm text-gray-700">{proj.folder_name}</span>
                                            <span className="text-xs text-gray-400 ml-auto">{proj.space_path}</span>
                                        </label>
                                    ))}
                                    {scopeOptions.projects.length === 0 && <p className="text-xs text-gray-400 italic p-2">No projects found</p>}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end mt-6 gap-3">
                        <button onClick={() => setStep('landing')} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                            Cancel
                        </button>
                        <button
                            onClick={handleNextToPreview}
                            className="px-5 py-2 bg-teal-700 text-white font-medium rounded-lg hover:bg-teal-800 transition-colors"
                        >
                            Next: Preview →
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // ─────────────────────────────────────────────────────
    // RENDER: Preview & Configure
    // ─────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Top bar */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => setStep('scope')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
                        <FaChevronLeft className="w-3 h-3" /> Scope
                    </button>
                    <span className="text-gray-300">|</span>
                    <span className="text-sm font-medium text-gray-800">Preview & Configure</span>
                    <InfoTip text="Toggle columns on the left to add/remove data from your report. The table updates instantly. When satisfied, export or save as a template." />
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span>{total} rows</span>
                    <span>•</span>
                    <span>{columnOrder.length} columns</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowSaveModal(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors"
                    >
                        <FaSave className="w-3 h-3" /> Save Template
                    </button>
                    <div className="relative">
                        <button
                            onClick={() => setShowExportMenu(!showExportMenu)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-teal-700 rounded-lg hover:bg-teal-800 transition-colors"
                        >
                            <FaDownload className="w-3 h-3" /> Export
                        </button>
                        {showExportMenu && (
                            <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-40">
                                <button onClick={() => handleExport('xlsx')} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 rounded-t-lg">
                                    Excel (.xlsx)
                                </button>
                                <button onClick={() => handleExport('csv')} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 rounded-b-lg border-t">
                                    CSV (.csv)
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Main content: sidebar + table */}
            <div className="flex flex-1 overflow-hidden">
                {/* Column selector sidebar */}
                <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto p-4 flex-shrink-0">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Columns
                        <InfoTip text="Check columns to add them to the report. Uncheck to remove. Each group represents a different analysis play." />
                    </h3>
                    {Object.entries(columnGroups).map(([group, cols]) => (
                        <div key={group} className="mb-4">
                            <h4 className="text-xs font-bold text-gray-600 uppercase mb-1.5">{group}</h4>
                            <div className="space-y-1">
                                {cols.map(col => (
                                    <label key={col.key} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={columns.includes(col.key)}
                                            onChange={() => toggleColumn(col.key)}
                                            className="text-teal-600 rounded"
                                        />
                                        <span className="text-xs text-gray-700 flex-1">
                                            {col.label}
                                            {columnCounts[col.key] != null && (
                                                <span className="text-gray-400 ml-1">({columnCounts[col.key]})</span>
                                            )}
                                        </span>
                                        <InfoTip text={col.description} />
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Table area */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Column order chips */}
                    <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-1.5 overflow-x-auto">
                        <span className="text-xs text-gray-400 mr-2 flex-shrink-0">Order:</span>
                        {columnOrder.map((key) => (
                            <span key={key} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-700 flex-shrink-0">
                                <FaGripVertical className="w-2 h-2 text-gray-300" />
                                {getColLabel(key)}
                                <button onClick={() => removeColumn(key)} className="ml-0.5 text-gray-400 hover:text-red-500">
                                    <FaTimes className="w-2.5 h-2.5" />
                                </button>
                            </span>
                        ))}
                    </div>

                    {/* Data table */}
                    <div className="flex-1 overflow-auto">
                        {previewLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent" />
                            </div>
                        ) : rows.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                                <HiDocumentReport className="w-12 h-12 mb-3 opacity-30" />
                                <p className="text-sm">No data matches your scope and column selection.</p>
                                <p className="text-xs mt-1">Try broadening your scope or adding more columns.</p>
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-gray-800 text-white">
                                    <tr>
                                        {columnOrder.map(key => (
                                            <th key={key} className="px-3 py-2.5 text-left text-xs font-medium whitespace-nowrap">
                                                {getColLabel(key)}
                                                <button
                                                    onClick={() => removeColumn(key)}
                                                    className="ml-1.5 text-gray-400 hover:text-red-300 opacity-0 group-hover:opacity-100"
                                                    title="Remove column"
                                                >
                                                    <FaTimes className="w-2.5 h-2.5 inline" />
                                                </button>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {rows.map((row, idx) => (
                                        <tr key={row._id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            {columnOrder.map(key => (
                                                <td key={key} className="px-3 py-2 text-gray-700 max-w-xs truncate" title={String(row[key] || '')}>
                                                    {String(row[key] || '—')}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {total > 0 && (
                        <div className="px-4 py-3 border-t border-gray-200 bg-white flex items-center justify-between text-sm">
                            <span className="text-gray-500">
                                Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handlePageChange(page - 1)}
                                    disabled={page <= 1}
                                    className="px-3 py-1 border rounded text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <FaChevronLeft className="w-3 h-3" />
                                </button>
                                <span className="text-gray-700 font-medium">Page {page} of {totalPages}</span>
                                <button
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page >= totalPages}
                                    className="px-3 py-1 border rounded text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <FaChevronRight className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Save Template Modal */}
            {showSaveModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
                        <h2 className="text-lg font-bold text-gray-900 mb-4">Save Report Template</h2>
                        <p className="text-xs text-gray-500 mb-4">
                            This template will be available to everyone in your organization.
                            <InfoTip text="Saved templates store your column selection, column order, and optionally your scope. Team members can load a template to instantly regenerate the same report with fresh data." />
                        </p>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    placeholder="e.g., Monthly Tariff Summary"
                                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                                <textarea
                                    value={templateDesc}
                                    onChange={(e) => setTemplateDesc(e.target.value)}
                                    placeholder="What is this report for?"
                                    rows={2}
                                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                                />
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={saveScope}
                                    onChange={(e) => setSaveScope(e.target.checked)}
                                    className="text-teal-600 rounded"
                                />
                                <span className="text-sm text-gray-700">Also save scope selection</span>
                                <InfoTip text="If unchecked, only the column configuration is saved. The scope will default to 'All projects' when loaded." />
                            </label>
                        </div>
                        <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
                            <span>Columns: {columns.length}</span>
                            <span>•</span>
                            <span>Scope: {scopeType === 'all' ? 'All' : scopeType === 'spaces' ? `${selectedSpaces.length} space(s)` : `${selectedProjects.length} project(s)`}</span>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button onClick={() => setShowSaveModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveTemplate}
                                className="px-4 py-2 bg-teal-700 text-white text-sm font-medium rounded-lg hover:bg-teal-800 transition-colors"
                            >
                                Save Template
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
